import { useCallback, useContext, useLayoutEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";

import { ServiceRequest } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Button, Center, Flex, Group, Menu, Pagination, rem, Stack, Title } from "@mantine/core";

import { IconChevronDown } from "@tabler/icons-react";

import { SectionHeaderRefresh } from "@/components/SectionHeaderRefresh";
import { TextWrapper } from "@/components/TextWrapper";
import { BackgroundContext } from "@/contexts/background";
import { LabOrderDetailsDrawer } from "@/health-gorilla";
import LabOrdersTable, { LabOrdersRows } from "@/health-gorilla/components/LabOrdersTable";
import { useLabOrderResults } from "@/health-gorilla/hooks/useLabOrderResults";
import { fetchLabOrderResultsForPractice } from "@/health-gorilla/services/labs";
import {
  HealthGorillaLabOrderInternalStatus,
  HealthGorillaLabOrderInternalStatusFilterDisplay,
  REVIEWED_AND_CLOSED_STATUSES,
} from "@/health-gorilla/types/labs";
import NotFound from "@/NotFound";
import PermissionGate from "@/permission/components/PermissionGate";
import { PROVIDER_SCRIBE_PERMISSION_FLAGS } from "@/permission/constants/practitioner-permissions";
import { RootState } from "@/store";
import { selectLabOrdersForPractice } from "@/store/slices/lab-orders";
import usePagination from "@/utils/hooks/use-pagination";

const MAX_WIDTH = rem(1440);
const PAGE_SIZE = 20;

export default function PracticeResults() {
  const { setBackground } = useContext(BackgroundContext);
  const medplum = useMedplum();
  const [searchParams, setSearchParams] = useSearchParams();
  const { practiceId } = useParams<{ practiceId: string }>();
  const results = useSelector<RootState, ServiceRequest[]>(selectLabOrdersForPractice(practiceId));
  const [selectedLabOrder, setSelectedLabOrder] = useState<ServiceRequest | undefined>();

  const { currentPage, changeCurrentPage, totalPages, changeTotalPages } = usePagination({
    pageSize: PAGE_SIZE,
  });

  useLayoutEffect(() => {
    setBackground("white");
  }, [setBackground]);

  const fetchPage = useCallback(async () => {
    if (!practiceId) return undefined;

    return fetchLabOrderResultsForPractice(
      medplum,
      practiceId,
      currentPage,
      PAGE_SIZE,
      searchParams,
    );
  }, [medplum, practiceId, currentPage, searchParams]);

  const { isLoading, isRefreshing, refresh } = useLabOrderResults({
    fetchPage,
    onTotalChange: changeTotalPages,
  });

  const handleCloseLabOrderDetailsDrawer = () => {
    setSelectedLabOrder(undefined);
  };

  const handleStatusChange = (value: string | null) => {
    if (value) {
      searchParams.set("status", value);
    } else {
      searchParams.delete("status");
    }

    // Clear pagination when status changes
    searchParams.delete("p");
    setSearchParams(searchParams, { preventScrollReset: true });
  };

  const statuses = useMemo(() => {
    return (searchParams.get("status")?.split(",") || [
      HealthGorillaLabOrderInternalStatus.All,
    ]) as [keyof typeof HealthGorillaLabOrderInternalStatusFilterDisplay];
  }, [searchParams]);

  return (
    <PermissionGate fallback={<NotFound />} requiredPermissions={PROVIDER_SCRIBE_PERMISSION_FLAGS}>
      <LabOrderDetailsDrawer
        opened={!!selectedLabOrder}
        labOrder={selectedLabOrder}
        onClose={handleCloseLabOrderDetailsDrawer}
      />
      <Center p="md" pb="0" mt="xl" mb="lg">
        <Stack miw={0} gap="lg" w="100%" maw={MAX_WIDTH}>
          <Group align="center" justify="space-between">
            <Group gap="xs" wrap="nowrap" align="center">
              <Title order={1}>Tests</Title>
              <SectionHeaderRefresh
                withTooltipArrow
                onRefresh={refresh}
                sectionLabel="Tests"
                isRefreshing={isRefreshing}
                tooltipLabel="Refresh Tests"
              />
            </Group>
            <Group>
              <TextWrapper variant="bold">Status</TextWrapper>
              <Menu withArrow shadow="sm" position="bottom-end">
                <Menu.Target>
                  <Button variant="outline" leftSection={<IconChevronDown size={16} />}>
                    {HealthGorillaLabOrderInternalStatusFilterDisplay[statuses[0]]}
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    onClick={() => handleStatusChange(HealthGorillaLabOrderInternalStatus.All)}
                  >
                    All
                  </Menu.Item>
                  <Menu.Item
                    onClick={() =>
                      handleStatusChange(HealthGorillaLabOrderInternalStatus.NeedsApproval)
                    }
                  >
                    Needs Approval
                  </Menu.Item>
                  <Menu.Item
                    onClick={() => handleStatusChange(HealthGorillaLabOrderInternalStatus.Ordered)}
                  >
                    Ordered
                  </Menu.Item>
                  <Menu.Item
                    onClick={() =>
                      handleStatusChange(HealthGorillaLabOrderInternalStatus.NeedsReview)
                    }
                  >
                    Needs Review
                  </Menu.Item>
                  <Menu.Item onClick={() => handleStatusChange(REVIEWED_AND_CLOSED_STATUSES)}>
                    Reviewed / Closed
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Group>
          <LabOrdersTable sortable showClientColumn isLoading={isLoading}>
            <LabOrdersRows showClientColumn results={results} onView={setSelectedLabOrder} />
          </LabOrdersTable>
          <Flex mb="xl" justify="center">
            <Pagination
              hideWithOnePage
              total={totalPages}
              value={currentPage}
              onChange={changeCurrentPage}
            />
          </Flex>
        </Stack>
      </Center>
    </PermissionGate>
  );
}
